<template>
  <m-form-group>
    <m-label v-if="labelText">{{ labelText }}</m-label>
    <m-input-group>
      <slot
        ref="datePickerFormInput"
        name="datePickerInputSlot"
        :clickable-readonly="!disabled"
        :value="selectedDateModel | filterSelector(language, dateFormat, placeholder)"
        :state="state"
        :placeholder="placeholder"
        :ev-mouseenter="
          () => {
            isResetIconShown = true;
          }
        "
        :ev-mouseleave="
          () => {
            isResetIconShown = false;
          }
        "
        :ev-click="
          () => {
            $emit('show-date-picker');
          }
        "
        :ev-blur="
          e => {
            checkBlur(e);
          }
        "
      >
        <m-form-input
          has-suffix-icon
          has-double-suffix-icon
          :readonly="readonly"
          :type="readonly ? 'text' : 'date'"
          :clickable-readonly="readonly && !disabled"
          :value="selectedDateModel | filterSelector(language, dateFormat, placeholder)"
          :state="state"
          :placeholder="placeholder"
          class="rounded"
          @mouseenter.native="isResetIconShown = true"
          @mouseleave.native="isResetIconShown = false"
          @click.native="$emit('show-date-picker')"
          @blur.native="checkBlur($event)"
          @input="!readonly ? checkDate($event) : null"
        />
      </slot>
      <b-form-invalid-feedback>
        {{ errorText }}
      </b-form-invalid-feedback>
      <m-icon
        ref="datePickerCalendarIcon"
        variant="picker-calendar"
        size="small"
        class="date-picker--no-outline"
        tabindex="0"
        is-suffix-icon
        @click.native="$emit('show-date-picker')"
        @mouseenter.native="isResetIconShown = true"
        @blur.native="checkBlur($event)"
      />
      <m-icon
        v-if="clearable && !disabled"
        ref="datePickerResetIcon"
        variant="reset-fill"
        size="small"
        class="date-picker--no-outline"
        tabindex="0"
        is-double-suffix-icon
        :class="isResetIconShown ? null : 'opacity--transparent'"
        @click.native="$emit('reset-selected-date')"
        @blur.native="checkBlur($event)"
        @mouseenter.native="isResetIconShown = true"
        @mouseleave.native="isResetIconShown = false"
      />
    </m-input-group>
    <small>
      {{ hintText }}
    </small>
  </m-form-group>
</template>
<script>
import MFormGroup from '../../FormGroup';
import MLabel from '../../Label';
import MInputGroup from '../../InputGroup';
import MFormInput from '../../FormInput';
import MIcon from '../../Icon';
import DatePickerMixins from '../mixins';
import locale from '../locale';
import { BFormInvalidFeedback } from 'bootstrap-vue';

export default {
  name: 'MDatePickerInput',
  components: {
    MFormGroup,
    MLabel,
    MInputGroup,
    MFormInput,
    MIcon,
    BFormInvalidFeedback,
  },
  filters: {
    filterSelector(date, language, dateFormat, placeholder) {
      if (date) {
        return locale.dateFormatBuilder(date, language, dateFormat);
      }
      return placeholder ? '' : '-';
    },
  },
  mixins: [DatePickerMixins],
  props: {
    selectedDate: {
      type: [Number, Array],
      default: 1,
    },
    selectedMonth: {
      type: [Number, Array],
      default: 0,
    },
    selectedYear: {
      type: [Number, Array],
      default: 2020,
    },
    dateFormat: {
      type: String,
      default: 'DD/MM/YYYY',
    },
    language: {
      type: String,
      default: 'en',
    },
    state: {
      type: Boolean,
      default: null,
    },
    errorText: {
      type: String,
      default: '',
    },
    hintText: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    labelText: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    readonly: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    isResetIconShown: false,
    selectedDateModel: null,
  }),
  watch: {
    selectedDate: {
      deep: true,
      handler(date) {
        if (this.getSelectedYear && this.getSelectedMonth >= 0 && date) {
          this.selectedDateModel = this.isRangedDatePicker && date.length === 2
            ? this.buildSelectedDate()
            : new Date(this.getSelectedYear, this.getSelectedMonth, date);
        } else {
          this.selectedDateModel = null;
        }
      },
    },
    selectedMonth: {
      deep: true,
      handler(month) {
        if (this.getSelectedYear && month >= 0 && this.getSelectedDate) {
          this.selectedDateModel = this.isRangedDatePicker && month.length === 2
            ? this.buildSelectedDate()
            : new Date(this.getSelectedYear, month, this.getSelectedDate);
        } else {
          this.selectedDateModel = null;
        }
      },
    },
    selectedYear: {
      deep: true,
      handler(year) {
        if (year && this.getSelectedMonth >= 0 && this.getSelectedDate) {
          this.selectedDateModel = this.isRangedDatePicker && year.length === 2
            ? this.buildSelectedDate()
            : new Date(year, this.getSelectedMonth, this.getSelectedDate);
        } else {
          this.selectedDateModel = null;
        }
      },
    },
  },
  computed: {
    isRangedDatePicker() {
      return Array.isArray(this.selectedDate);
    },
    getSelectedDate() {
      return this.isRangedDatePicker ? this.selectedDate[0] : this.selectedDate;
    },
    getSelectedMonth() {
      return this.isRangedDatePicker ? this.selectedMonth[0] : this.selectedMonth;
    },
    getSelectedYear() {
      return this.isRangedDatePicker ? this.selectedYear[0] : this.selectedYear;
    },
  },
  methods: {
    checkDate(event) {
      if (Date.parse(event) > 0) { this.$emit('input', event); };
    },
    buildSelectedDate() {
      if (this.selectedDate[1]) {
        return [
          new Date(this.selectedYear[0], this.selectedMonth[0], this.selectedDate[0]),
          new Date(this.selectedYear[1], this.selectedMonth[1], this.selectedDate[1]),
        ];
      }
      return [
        new Date(this.selectedYear[0], this.selectedMonth[0], this.selectedDate[0]),
        null,
      ];
    },
  },
};
</script>
